import { useQuery } from '@tanstack/react-query';
import { incidents } from '@cwos/api-client';
import { Pill, Refused, ScopeNote, SuccessEmpty } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { useNav } from '../navigation.js';
import {
  CATEGORY_READING,
  SEVERITY_READING,
  STATUS_READING,
  emptyIncidentSentence,
  formatWhen,
  incidentPopulation,
  incidentScopeNote,
  notificationReading,
  participantSentence,
} from '../compliance/incidentsLogic.js';

/**
 * OPEN INCIDENTS, ON THE LANDING SCREEN — for the supervisor who needs to know
 * before they hear it somewhere else.
 *
 * ===========================================================================
 * WHY THIS IS A SEPARATE CARD AND NOT A ROW IN "WHAT NEEDS YOU"
 * ===========================================================================
 * `1765000000000_an-incident-is-not-an-alert.sql` is the title of the ruling
 * this product runs on, and folding an incident into the alert queue is
 * precisely what it forbids in the data model. The two are different objects:
 *
 *   AN ALERT is Phibian noticing something. A rule fired. Nobody wrote it,
 *   nobody signed it, and the act it invites is ACKNOWLEDGEMENT — "got it" —
 *   which is why the card above this one carries a Got it button on every row.
 *
 *   AN INCIDENT is a PERSON saying what happened. It has an author, a narrative
 *   against an approved instrument, an append-only history, and it ends when
 *   somebody writes down how it was resolved and signs that. There is no
 *   equivalent of "got it": acknowledging an incident is not a state the domain
 *   has, and offering one here would be a control the server would refuse.
 *
 * So this card has NO acknowledge affordance, no dismiss, and exactly one link
 * per row — into the register, where the acts that do exist live. It reads as a
 * list of things that happened, because that is what it is.
 *
 * ===========================================================================
 * THE FOUR STATES, AND WHY THE EMPTY ONE IS THE DANGEROUS ONE
 * ===========================================================================
 * `GET /api/incidents` narrows its rows IN SQL by the caller's reach — the
 * incidents they filed, plus those about a client on their caseload, unless
 * they hold `incidents:read_all` AND `directory:read_all` together. It sends no
 * total and no `scope`, so an unreachable incident is simply ABSENT.
 *
 * "No open incidents" on a supervisor's landing screen is therefore a safety
 * claim that is false for most readers, and the three ways of arriving at a
 * blank card have to look different:
 *
 *   REFUSED — 403. `incidents:read`, or the `directory:read` every route in the
 *             domain also requires. An ANSWER about this account; no retry.
 *   EMPTY   — the server answered and there were none THAT THIS READER REACHES,
 *             which `emptyIncidentSentence` says in three different ways
 *             depending on how far that reach goes.
 *   FAILED  — the read did not complete. Retryable, and explicitly not an
 *             all-clear.
 *   LOADING — nothing known yet.
 *
 * The narrowing note sits under the rows for the same reason it does on the
 * register: a count taken from this list is a claim about the caller's reach
 * and never about the organization.
 */

/**
 * The statuses that mean somebody still owes something.
 *
 * `resolved` is NOT among them, and that is a decision rather than an
 * oversight: `incident_resolution_ck` makes `resolved` a written account with a
 * time and an author on it, so the work is done even though the record is not
 * closed. `closed` is terminal. Both belong in the register, neither belongs on
 * a screen whose question is "what is still open".
 */
const OPEN_STATUSES: readonly incidents.IncidentStatus[] = [
  'reported',
  'under_review',
  'action_taken',
];

export function openIncidentsOf(rows: readonly incidents.IncidentView[]): incidents.IncidentView[] {
  const rank: Record<incidents.IncidentSeverity, number> = {
    critical: 0,
    high: 1,
    moderate: 2,
    low: 3,
  };
  return rows
    .filter((i) => OPEN_STATUSES.includes(i.status))
    .sort(
      (a, b) =>
        rank[a.severity] - rank[b.severity] ||
        // Most recent first within a severity: the thing that happened an hour
        // ago is the thing a supervisor is about to be asked about.
        b.occurredAt.localeCompare(a.occurredAt),
    );
}

export function OpenIncidents() {
  const permissions = usePermissions();
  const navigate = useNav();

  const list = useQuery({
    queryKey: ['incidents', 'open'],
    // Asked WITHOUT a status filter and narrowed here. The route takes one
    // status at a time and "open" is three of them, so a filtered read would be
    // three requests whose union this component would have to reassemble — and
    // a partial failure among them would produce a count that looks whole.
    queryFn: () => incidents.list({ limit: 200 }),
    refetchInterval: 120_000,
  });

  const open = openIncidentsOf(list.data ?? []);
  // `listPhase` is asked about the OPEN rows, not the raw ones: an organization
  // whose every incident is closed has nothing open, and that is the empty
  // state rather than a list of nothing.
  const phase = listPhase(list, open.length);
  const population = incidentPopulation(permissions);
  const note = incidentScopeNote(population);
  const top = open.slice(0, 4);

  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">Incidents still open</h2>
        {open.length > 0 ? (
          <button
            type="button"
            className="rn-link"
            onClick={() => navigate('/compliance/incidents')}
          >
            All {open.length} →
          </button>
        ) : null}
      </div>
      {/* THE SENTENCE THAT KEEPS THIS CARD FROM READING AS THE ONE ABOVE IT. */}
      <p className="rn-muted" style={{ marginBottom: 'var(--space-8)' }}>
        Reported by a person, not raised by a rule. Each one ends with somebody writing down how it
        was resolved.
      </p>

      <ListState
        phase={phase}
        surface="table"
        rows={3}
        failedMessage="We couldn’t read open incidents just now. Nothing here says there are none."
        onRetry={() => void list.refetch()}
        refused={
          <Refused
            heading="Incidents are not shown to you"
            message="Reading incidents needs the incidents:read permission and permission to view the workforce directory, because an incident names the person who reported it and the worker involved. This is withheld, not empty — do not read it as an organization with nothing open."
          />
        }
        empty={
          <SuccessEmpty
            heading={
              population === 'organization' ? 'Nothing open' : 'Nothing open that you can see'
            }
            message={emptyIncidentSentence(population)}
          />
        }
      >
        <ul
          className="rn-col"
          style={{ gap: 'var(--space-12)', paddingLeft: 0, listStyle: 'none' }}
        >
          {top.map((incident) => {
            const severity = SEVERITY_READING[incident.severity];
            const status = STATUS_READING[incident.status];
            const notified = notificationReading(incident);
            return (
              <li key={incident.id} style={{ display: 'grid', gap: 'var(--space-4)' }}>
                <span className="rn-row" style={{ gap: 'var(--space-8)', flexWrap: 'wrap' }}>
                  <Pill variant={severity.variant} dot>
                    {severity.label}
                  </Pill>
                  <Pill variant={status.variant} dot>
                    {status.label}
                  </Pill>
                  <button
                    type="button"
                    className="rn-link"
                    onClick={() => navigate(`/compliance/incidents/${incident.id}`)}
                  >
                    {CATEGORY_READING[incident.category]} · {formatWhen(incident.occurredAt)}
                  </button>
                </span>
                <span className="rn-muted">
                  About {participantSentence(incident)} · filed by {incident.reportedBy.displayName}
                </span>
                {/* A NEGATIVE WORTH SHOWING, and on this screen more than any
                    other: the pair is enforced by the database, so "nobody has
                    been told" is a recorded fact and not a blank cell — and it
                    is the exact row a supervisor is looking for before somebody
                    else tells them. */}
                <span className="rn-muted">
                  {notified.kind === 'notified'
                    ? `Management told ${formatWhen(notified.when)}`
                    : 'Management has not been notified'}
                </span>
              </li>
            );
          })}
        </ul>
      </ListState>

      {note ? <ScopeNote scope={note} /> : null}
    </section>
  );
}
